import {
    type ApiResponse,
    BackendClient,
    BackendClientParameters,
} from "@internal/backstage-plugin-utility-common";

import type {
    ListWorkflowExecutionsResponse,
    SignalWorkflowRequest,
    SignalWorkflowResponse,
    StartWorkflowRequest,
    StartWorkflowResponse,
    TemporalQueryStatus,
    WorkflowExecutionDescription,
} from "../types";

/**
 * Client for interacting with the Temporal backend plugin
 *
 * @public
 */
export class TemporalBackendClient extends BackendClient {
    private oktaToken?: string;

    /**
     * Creates a new TemporalBackendClient instance
     *
     * @param options - Configuration options, targetPluginId will be set to 'temporal'
     */
    constructor(options: Omit<BackendClientParameters, "targetPluginId">) {
        super({
            ...options,
            targetPluginId: "temporal",
        });
    }

    /**
     * Set the Okta access token for Temporal authentication
     *
     * @param token - Okta access token obtained from oktaAuthApiRef.getAccessToken()
     *
     * @remarks
     * This token will be sent with all subsequent requests to the Temporal backend.
     * Call this method after obtaining the token from oktaAuthApiRef in your frontend code.
     */
    setOktaToken(token: string): void {
        this.oktaToken = token;
    }

    /**
     * Get headers with Okta token included
     *
     * @param additionalHeaders - Optional additional headers to merge
     * @returns Headers object with X-Okta-Token set
     */
    private getHeaders(
        additionalHeaders?: Record<string, string>
    ): Record<string, string> {
        const headers: Record<string, string> = { ...additionalHeaders };
        if (this.oktaToken) {
            headers["X-Okta-Token"] = this.oktaToken;
        }
        return headers;
    }

    /**
     * List workflow executions filtered by workflow type and optionally by status
     *
     * @param workflowType - The workflow type to filter by (required)
     * @param status - Optional workflow execution status to filter by (e.g., 'Running', 'Completed')
     * @param pageSize - Optional page size (default: 50, max: 1000)
     * @param nextPageToken - Optional pagination token for the next page
     * @returns Promise resolving to the list of workflow executions
     */
    async listWorkflows(
        workflowType: string,
        status?: TemporalQueryStatus,
        pageSize?: number,
        nextPageToken?: string
    ): Promise<ApiResponse<ListWorkflowExecutionsResponse>> {
        const params = new URLSearchParams();
        params.append("workflowType", workflowType);

        if (status) {
            params.append("status", status);
        }
        if (pageSize) {
            params.append("pageSize", pageSize.toString());
        }
        if (nextPageToken) {
            params.append("nextPageToken", nextPageToken);
        }

        return this.api(`/workflows?${params.toString()}`, {
            headers: this.getHeaders(),
        });
    }

    /**
     * Get detailed information about a specific workflow execution
     *
     * @param workflowId - The workflow ID
     * @param runId - Optional run ID for a specific execution
     * @returns Promise resolving to the workflow execution description
     */
    async getWorkflow(
        workflowId: string,
        runId?: string
    ): Promise<ApiResponse<WorkflowExecutionDescription>> {
        const params = runId ? `?runId=${encodeURIComponent(runId)}` : "";
        return this.api(
            `/workflows/${encodeURIComponent(workflowId)}${params}`,
            {
                headers: this.getHeaders(),
            }
        );
    }

    /**
     * Send a signal to a workflow execution
     *
     * @param request - The signal request with workflowId, signalName, args, and optional runId
     * @returns Promise resolving to the signal response
     */
    async signalWorkflow(
        request: SignalWorkflowRequest
    ): Promise<ApiResponse<SignalWorkflowResponse>> {
        return this.api(
            `/workflows/${encodeURIComponent(request.workflowId)}/signal`,
            {
                method: "POST",
                headers: this.getHeaders({
                    "Content-Type": "application/json",
                }),
                body: JSON.stringify({
                    signalName: request.signalName,
                    args: request.args,
                    runId: request.runId,
                }),
            }
        );
    }

    /**
     * Start a new workflow execution (non-blocking)
     *
     * This method starts a Temporal workflow and returns IMMEDIATELY without waiting for completion.
     * The workflow continues running in the background.
     *
     * **Use this for:**
     * - Long-running workflows (minutes, hours, or days)
     * - Approval workflows that require human interaction
     * - Fire-and-forget operations
     * - Workflows tracked separately via UI or polling
     *
     * **Returns:**
     * - `workflowId`: Unique identifier for the workflow execution
     * - `firstExecutionRunId`: First execution run ID (for tracking)
     * - Does NOT include the workflow result
     *
     * **Example:**
     * ```typescript
     * // Start approval workflow (runs in background)
     * const response = await client.startWorkflow({
     *   workflowType: 'gcpProjectApproval',
     *   taskQueue: 'approvals',
     *   args: [{ projectId: 'my-project' }],
     *   searchAttributes: { ApprovalStatus: ['PENDING'] }
     * });
     *
     * if (response.body.ok) {
     *   console.log(`Workflow started: ${response.body.data.workflowId}`);
     *   // Workflow continues running, user can track it in Temporal UI
     * }
     * ```
     *
     * @param request - The workflow start request with workflowType, taskQueue, args, etc.
     * @returns Promise resolving to the workflow start response with workflowId and firstExecutionRunId
     *
     */
    async startWorkflow(
        request: StartWorkflowRequest
    ): Promise<ApiResponse<StartWorkflowResponse>> {
        return this.api("/workflows/start", {
            method: "POST",
            headers: this.getHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify(request),
        });
    }
}
